import { useState, useEffect, useCallback } from 'react';

const ACTIVATION_LINE_PX = 120;
/** Tolerance for `scrollHeight` vs client measurements (subpixel, mobile chrome). */
const DOC_BOTTOM_EPSILON_PX = 4;

function pickActiveSection(sectionIds) {
  let current = sectionIds[0];
  for (const id of sectionIds) {
    const el = document.getElementById(id);
    if (!el) continue;
    const { top } = el.getBoundingClientRect();
    if (top <= ACTIVATION_LINE_PX) {
      current = id;
    }
  }

  const lastId = sectionIds[sectionIds.length - 1];
  const lastEl = lastId ? document.getElementById(lastId) : null;
  if (lastEl) {
    const lastTop = lastEl.getBoundingClientRect().top;
    const scrollBottom = window.scrollY + window.innerHeight;
    const docBottom = document.documentElement.scrollHeight;
    const atDocumentBottom = scrollBottom >= docBottom - DOC_BOTTOM_EPSILON_PX;
    if (atDocumentBottom && lastTop > ACTIVATION_LINE_PX) {
      return lastId;
    }
  }

  return current;
}

/**
 * Highlights the TOC entry for the section currently in view.
 * Uses IntersectionObserver to react to scroll/layout; active id is derived from
 * viewport position (last section whose top crossed the activation line).
 */
export function useActiveSection(sectionIds) {
  const [activeId, setActiveId] = useState(sectionIds[0] || '');

  const sync = useCallback(() => {
    if (!sectionIds.length) return;
    const next = pickActiveSection(sectionIds);
    setActiveId((prev) => (prev === next ? prev : next));
  }, [sectionIds]);

  useEffect(() => {
    if (!sectionIds.length) return undefined;

    const elements = sectionIds.map((id) => document.getElementById(id)).filter(Boolean);
    if (!elements.length) return undefined;

    sync();

    const observer = new IntersectionObserver(
      () => {
        sync();
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: [0, 0.05, 0.25, 0.5, 0.75, 1]
      }
    );

    elements.forEach((el) => observer.observe(el));
    window.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
    };
  }, [sectionIds, sync]);

  return activeId;
}
